/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */
import React, { forwardRef } from 'react'

import type { FieldValues, UseFormProps, UseFormReturn } from 'react-hook-form'
import { FormProvider, useForm } from 'react-hook-form'

export interface FormProps<TFieldValues extends FieldValues> extends Omit<React.ComponentProps<'form'>, 'onSubmit'> {
  error?: unknown
  /**
   * The methods returned by `useForm`.
   * This prop is only necessary if you've called `useForm` yourself to get
   * access to one of its functions, like `reset`.
   *
   * @example
   *
   * ```typescript
   * const formMethods = useForm<FormData>()
   *
   * const onSubmit = (data: FormData) => {
   *  sendDataToServer(data)
   *  formMethods.reset()
   * }
   *
   * return (
   *   <Form formMethods={formMethods} onSubmit={onSubmit}>
   * )
   * ```
   */
  formMethods?: UseFormReturn<TFieldValues>
  /**
   * Configures how React Hook Form performs validation, among other things.
   *
   * @example
   *
   * ```jsx
   * <Form config={{ mode: 'onBlur' }}>
   * ```
   *
   * @see {@link https://react-hook-form.com/api/useform}
   */
  config?: UseFormProps<TFieldValues>
  onSubmit?: (value: TFieldValues, event?: React.BaseSyntheticEvent) => void
}

function FormComponent<TFieldValues extends FieldValues>(
  props: FormProps<TFieldValues>,
  ref: React.ForwardedRef<HTMLFormElement>
) {
  const { config, formMethods: propFormMethods, onSubmit, children, ...rest } = props
  const hookFormMethods = useForm<TFieldValues>(config)
  const formMethods = propFormMethods || hookFormMethods

  return (
    <form ref={ref} {...rest} onSubmit={formMethods.handleSubmit((data, event) => onSubmit?.(data, event))}>
      <FormProvider {...formMethods}>{children}</FormProvider>
    </form>
  )
}

/**
 * Renders a `<form>` with the required context.
 */
export const Form = forwardRef(FormComponent) as <TFieldValues extends FieldValues>(
  props: FormProps<TFieldValues> & React.RefAttributes<HTMLFormElement>
) => React.ReactElement
